/**
 * Feb 2026 · Mail Profiles — per-workspace, per-brand sender identities.
 * Each row = one branded outbound relay (SMTP host/port/user/pass, or a
 * Resend / ZeptoMail API key). Forms + Studio picks one via
 * `mail_profile_id`. Secrets are never returned by GET — the API surfaces
 * `has_smtp_password` / `has_resend_api_key` / `has_zeptomail_api_key`
 * booleans so the UI can show a "password saved" pill.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Send, Mail, Check, Globe } from "lucide-react";

const EMPTY = {
  name: "", provider: "smtp", sender_email: "", from_name: "",
  reply_to_email: "", reply_to_name: "",
  smtp_host: "", smtp_port: 587, smtp_user: "", smtp_password: "", smtp_tls: "starttls",
  resend_api_key: "",
  zeptomail_api_key: "", zeptomail_region: "in",
  is_default: false, active: true,
};

export default function MailProfilesSettings() {
  const { activeWorkspaceId: ws } = useAuth();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(null); // profile being tested
  const [dnsFor, setDnsFor] = useState(null);   // profile whose DNS to inspect
  const load = async () => {
    if (!ws) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${ws}/mail-profiles`);
      setRows(data.profiles || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ws]);
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? Forms/studio pointing to it will fall back to the workspace default.`)) return;
    try {
      await api.delete(`/workspaces/${ws}/mail-profiles/${r.id}`);
      toast.success("Profile deleted"); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  return (
    <div className="p-4 sm:p-6 md:px-10 max-w-5xl" data-testid="mail-profiles-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Mail className="w-5 h-5" /> Mail Profiles</h1>
          <p className="text-xs text-muted-foreground max-w-2xl">
            Named outbound-email identities. Each form or studio module can point at a specific profile
            so campaigns match the sender domain + brand. Supports SMTP, Resend, and ZeptoMail.
          </p>
        </div>
        <Button className="rounded-none" onClick={() => setEditing({ ...EMPTY })} data-testid="mail-profiles-new">
          <Plus className="w-4 h-4 mr-1" /> New profile
        </Button>
      </div>

      {loading ? <div className="text-sm italic text-muted-foreground">Loading…</div> :
        rows.length === 0 ? (
          <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No mail profiles yet. Add one to send from a branded domain.
          </div>
        ) : (
          <div className="border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted/50">
                <tr>
                  {["Name", "Provider", "From", "Reply-to", "Default", "Status", ""].map((h) => (
                    <th key={h} className="text-left px-2 py-1.5 uppercase tracking-widest font-semibold text-[10px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`mail-profile-row-${r.id}`}>
                    <td className="px-2 py-1.5 font-semibold">{r.name}</td>
                    <td className="px-2 py-1.5 uppercase text-[10px]">{r.provider}</td>
                    <td className="px-2 py-1.5">{r.from_name ? `${r.from_name} <${r.sender_email}>` : r.sender_email}</td>
                    <td className="px-2 py-1.5">{r.reply_to_email || "—"}</td>
                    <td className="px-2 py-1.5">{r.is_default ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : ""}</td>
                    <td className="px-2 py-1.5">
                      {r.active ? <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700">Active</span>
                                 : <span className="text-[10px] px-1.5 py-0.5 bg-neutral-200 text-neutral-700">Inactive</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="rounded-none h-7 text-[11px]"
                              onClick={() => setDnsFor(r)} data-testid={`mail-profile-dns-${r.id}`}>
                        <Globe className="w-3.5 h-3.5 mr-1" /> DNS
                      </Button>
                      <Button size="sm" variant="ghost" className="rounded-none h-7 text-[11px]"
                              onClick={() => setTesting(r)} data-testid={`mail-profile-test-${r.id}`}>
                        <Send className="w-3.5 h-3.5 mr-1" /> Test
                      </Button>
                      <Button size="sm" variant="ghost" className="rounded-none h-7 text-[11px]"
                              onClick={() => setEditing({ ...EMPTY, ...r })} data-testid={`mail-profile-edit-${r.id}`}>Edit</Button>
                      <Button size="sm" variant="ghost" className="rounded-none h-7 text-[11px] text-rose-600 hover:text-rose-800"
                              onClick={() => del(r)} data-testid={`mail-profile-del-${r.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {editing && <ProfileEditor ws={ws} row={editing} onClose={() => setEditing(null)}
                                   onSaved={() => { setEditing(null); load(); }} />}
      {testing && <TestSendDialog ws={ws} row={testing} onClose={() => setTesting(null)} />}
      {dnsFor && <DnsRecordsDialog ws={ws} row={dnsFor} onClose={() => setDnsFor(null)} />}
    </div>
  );
}

function ProfileEditor({ ws, row, onClose, onSaved }) {
  const [f, setF] = useState({ ...row });
  const [busy, setBusy] = useState(false);
  const upd = (patch) => setF((p) => ({ ...p, ...patch }));
  const save = async () => {
    setBusy(true);
    try {
      // Strip nulls the API doesn't expect
      const body = { ...f };
      if (!body.smtp_password) delete body.smtp_password;
      if (!body.resend_api_key) delete body.resend_api_key;
      if (!body.zeptomail_api_key) delete body.zeptomail_api_key;
      if (!body.reply_to_email) delete body.reply_to_email;
      if (body.id) await api.patch(`/workspaces/${ws}/mail-profiles/${body.id}`, body);
      else         await api.post (`/workspaces/${ws}/mail-profiles`, body);
      toast.success(body.id ? "Profile updated" : "Profile created");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-xl" data-testid="mail-profile-editor">
        <DialogHeader>
          <DialogTitle>{f.id ? "Edit" : "New"} mail profile</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Row label="Profile name">
            <Input value={f.name} onChange={(e) => upd({ name: e.target.value })}
                    placeholder="Story & Fame · outbound"
                    className="rounded-none h-9" data-testid="mail-profile-name" />
          </Row>
          <Row label="Provider">
            <div className="flex gap-1">
              {["smtp", "resend", "zeptomail"].map((p) => (
                <button key={p} onClick={() => upd({ provider: p })}
                         data-testid={`mail-profile-provider-${p}`}
                         className={`px-3 py-1 text-xs border ${f.provider === p ? "bg-primary text-white border-primary" : "border-border hover:bg-muted"} uppercase`}>{p}</button>
              ))}
            </div>
          </Row>
          <Row label="Sender email (must be verified with your provider)">
            <Input value={f.sender_email} onChange={(e) => upd({ sender_email: e.target.value })}
                    placeholder="hello@yourdomain.com"
                    className="rounded-none h-9" data-testid="mail-profile-from-email" />
          </Row>
          <div className="grid grid-cols-2 gap-2">
            <Row label="From name">
              <Input value={f.from_name || ""} onChange={(e) => upd({ from_name: e.target.value })}
                      placeholder="Story & Fame Studio"
                      className="rounded-none h-9" data-testid="mail-profile-from-name" />
            </Row>
            <Row label="Reply-to name">
              <Input value={f.reply_to_name || ""} onChange={(e) => upd({ reply_to_name: e.target.value })}
                      placeholder="Studio Ops"
                      className="rounded-none h-9" data-testid="mail-profile-reply-name" />
            </Row>
          </div>
          <Row label="Reply-to email (optional)">
            <Input value={f.reply_to_email || ""} onChange={(e) => upd({ reply_to_email: e.target.value })}
                    placeholder="studio@yourdomain.com"
                    className="rounded-none h-9" data-testid="mail-profile-reply-email" />
          </Row>

          {f.provider === "smtp" && (
            <div className="space-y-2 border border-dashed border-border p-3">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">SMTP credentials</div>
              <div className="grid grid-cols-3 gap-2">
                <Row label="Host" col="col-span-2">
                  <Input value={f.smtp_host || ""} onChange={(e) => upd({ smtp_host: e.target.value })}
                          placeholder="smtp.zoho.in"
                          className="rounded-none h-9" data-testid="mail-profile-smtp-host" />
                </Row>
                <Row label="Port">
                  <Input type="number" value={f.smtp_port || 587} onChange={(e) => upd({ smtp_port: Number(e.target.value) })}
                          className="rounded-none h-9" data-testid="mail-profile-smtp-port" />
                </Row>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Row label="Username">
                  <Input value={f.smtp_user || ""} onChange={(e) => upd({ smtp_user: e.target.value })}
                          placeholder="postmaster@…"
                          className="rounded-none h-9" data-testid="mail-profile-smtp-user" />
                </Row>
                <Row label={f.has_smtp_password ? "Password (leave blank to keep)" : "Password"}>
                  <Input type="password" value={f.smtp_password || ""}
                          onChange={(e) => upd({ smtp_password: e.target.value })}
                          placeholder={f.has_smtp_password ? "••••••••" : "app password"}
                          className="rounded-none h-9" data-testid="mail-profile-smtp-pass" />
                </Row>
              </div>
              <Row label="Encryption">
                <select value={f.smtp_tls || "starttls"} onChange={(e) => upd({ smtp_tls: e.target.value })}
                         className="w-full h-9 border border-border bg-background text-xs rounded-none px-2" data-testid="mail-profile-smtp-tls">
                  <option value="starttls">STARTTLS (port 587)</option>
                  <option value="ssl">SSL/TLS (port 465)</option>
                  <option value="none">None (port 25 — not recommended)</option>
                </select>
              </Row>
            </div>
          )}

          {f.provider === "resend" && (
            <Row label={f.has_resend_api_key ? "Resend API key (leave blank to keep existing)" : "Resend API key"}>
              <Input type="password" value={f.resend_api_key || ""}
                      onChange={(e) => upd({ resend_api_key: e.target.value })}
                      placeholder={f.has_resend_api_key ? "•••• (saved)" : "re_..."}
                      className="rounded-none h-9 font-mono" data-testid="mail-profile-resend-key" />
            </Row>
          )}
          {f.provider === "zeptomail" && (
            <>
              <Row label="ZeptoMail region">
                <select value={f.zeptomail_region || "in"} onChange={(e) => upd({ zeptomail_region: e.target.value })}
                         className="w-full h-9 border border-border bg-background text-xs rounded-none px-2" data-testid="mail-profile-zepto-region">
                  <option value="in">India (api.zeptomail.in)</option>
                  <option value="us">US (api.zeptomail.com)</option>
                  <option value="eu">EU (api.zeptomail.eu)</option>
                </select>
              </Row>
              <Row label={f.has_zeptomail_api_key ? "ZeptoMail Send token (leave blank to keep)" : "ZeptoMail Send token"}>
                <Input type="password" value={f.zeptomail_api_key || ""}
                        onChange={(e) => upd({ zeptomail_api_key: e.target.value })}
                        placeholder={f.has_zeptomail_api_key ? "•••• (saved)" : "Zoho-enczapikey ..."}
                        className="rounded-none h-9 font-mono" data-testid="mail-profile-zepto-key" />
              </Row>
            </>
          )}

          <div className="flex items-center gap-4 pt-1">
            <label className="text-[11px] flex items-center gap-2">
              <input type="checkbox" checked={!!f.is_default}
                      onChange={(e) => upd({ is_default: e.target.checked })}
                      data-testid="mail-profile-default" />
              Workspace default
            </label>
            <label className="text-[11px] flex items-center gap-2">
              <input type="checkbox" checked={f.active !== false}
                      onChange={(e) => upd({ active: e.target.checked })}
                      data-testid="mail-profile-active" />
              Active
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy || !f.name || !f.sender_email}
                  data-testid="mail-profile-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TestSendDialog({ ws, row, onClose }) {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [retryingId, setRetryingId] = useState(null);
  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/workspaces/${ws}/mail-profiles/${row.id}/test-sends`);
      setHistory(data.test_sends || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [row.id]);
  const run = async () => {
    if (!to) { toast.error("Enter a recipient email"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/workspaces/${ws}/mail-profiles/${row.id}/test`, { to });
      setResult(data.row || data);
      if (data.ok) toast.success("Test email sent — check the inbox");
      else toast.error(`Test failed · ${(data.row?.error || "").slice(0, 100)}`);
      await loadHistory();
    } catch (e) { toast.error(apiErrorMessage(e, "Test failed")); setResult({ error: apiErrorMessage(e) }); }
    finally { setBusy(false); }
  };
  const retry = async (id) => {
    setRetryingId(id);
    try {
      const { data } = await api.post(`/workspaces/${ws}/mail-profiles/${row.id}/test-sends/${id}/retry`, {});
      if (data.ok) toast.success("Retry sent");
      else toast.error(`Retry failed · ${(data.row?.error || "").slice(0, 100)}`);
      await loadHistory();
    } catch (e) { toast.error(apiErrorMessage(e, "Retry failed")); }
    finally { setRetryingId(null); }
  };
  const statusPill = (s) => {
    const cls = s === "sent" ? "bg-emerald-50 border-emerald-500 text-emerald-800"
              : s === "failed" ? "bg-rose-50 border-rose-500 text-rose-800"
              : "bg-muted border-border text-muted-foreground";
    return <span className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${cls}`}>{s}</span>;
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-2xl" data-testid="mail-profile-test-dialog">
        <DialogHeader><DialogTitle>Test send · {row.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs">Send test email to</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="your@email.com"
                      className="rounded-none h-9 mt-1" data-testid="mail-profile-test-to" />
            </div>
            <Button className="rounded-none h-9" onClick={run} disabled={busy} data-testid="mail-profile-test-run">
              {busy ? "Sending…" : "Send test"}
            </Button>
          </div>
          {result && result.status === "failed" && (
            <div className="text-[11px] border border-rose-500 bg-rose-50 text-rose-800 p-2">
              {result.error || "Send failed — check credentials"}
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-1">History (latest 100)</div>
            <div className="border border-border max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1 font-semibold uppercase tracking-widest text-[10px]">When</th>
                    <th className="text-left px-2 py-1 font-semibold uppercase tracking-widest text-[10px]">To</th>
                    <th className="text-left px-2 py-1 font-semibold uppercase tracking-widest text-[10px]">Status</th>
                    <th className="text-right px-2 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-t border-border" data-testid={`mail-profile-test-row-${h.id}`}>
                      <td className="px-2 py-1 font-mono text-[10px]">{h.sent_at?.slice(0, 19).replace("T", " ")}</td>
                      <td className="px-2 py-1">{h.to_email}{h.retry_of && <span className="text-[9px] text-muted-foreground ml-1">(retry)</span>}</td>
                      <td className="px-2 py-1">{statusPill(h.status)}
                        {h.error && <div className="text-[9px] text-rose-700 truncate max-w-[200px]" title={h.error}>{h.error}</div>}
                      </td>
                      <td className="px-2 py-1 text-right">
                        <button onClick={() => retry(h.id)} disabled={retryingId === h.id}
                                data-testid={`mail-profile-test-retry-${h.id}`}
                                className="text-[10px] text-primary hover:underline disabled:opacity-50">
                          {retryingId === h.id ? "…" : "Retry"}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {history.length === 0 && <tr><td colSpan={4} className="text-center py-4 text-muted-foreground italic text-[11px]">No test sends yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Feb 2026 · DNS-record helper — shows SPF/DKIM/DMARC records with copy
// buttons and a "Check DNS" verify action.
function DnsRecordsDialog({ ws, row, onClose }) {
  const [rec, setRec] = useState(null);
  const [checks, setChecks] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyCheck, setBusyCheck] = useState(false);
  useEffect(() => {
    setBusy(true);
    api.get(`/workspaces/${ws}/mail-profiles/${row.id}/dns-records`)
      .then((r) => setRec(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Load failed")))
      .finally(() => setBusy(false));
    /* eslint-disable-next-line */
  }, [row.id]);
  const runCheck = async () => {
    setBusyCheck(true); setChecks(null);
    try {
      const { data } = await api.post(`/workspaces/${ws}/mail-profiles/${row.id}/check-dns`, {});
      setChecks(data);
      if (data.all_ok) toast.success("All DNS records look correct 🎉");
      else toast.error("Some records are missing — see verdicts below");
    } catch (e) { toast.error(apiErrorMessage(e, "DNS check failed")); }
    finally { setBusyCheck(false); }
  };
  const findVerdict = (r) => (checks?.checks || []).find((c) => c.host === r.host && c.kind === r.kind);
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-3xl" data-testid="mail-profile-dns-dialog">
        <DialogHeader>
          <DialogTitle>DNS records · {row.name}</DialogTitle>
        </DialogHeader>
        {busy && <div className="text-xs italic text-muted-foreground p-4 text-center">Loading…</div>}
        {rec && rec.warning && (
          <div className="text-[11px] border border-amber-500 bg-amber-50 text-amber-800 p-2">{rec.warning}</div>
        )}
        {rec && rec.domain && (
          <>
            <div className="flex items-center justify-between text-xs">
              <div>Sender domain: <b className="font-mono">{rec.domain}</b> · Provider: <b>{rec.provider}</b></div>
              <Button size="sm" className="rounded-none h-7" onClick={runCheck} disabled={busyCheck} data-testid="mail-profile-dns-check">
                {busyCheck ? "Checking…" : "Check DNS"}
              </Button>
            </div>
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-widest text-[10px]">Kind</th>
                    <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-widest text-[10px]">Host</th>
                    <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-widest text-[10px]">Type</th>
                    <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-widest text-[10px]">Value</th>
                    <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-widest text-[10px]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rec.records.map((r, i) => {
                    const v = findVerdict(r);
                    return (
                      <tr key={i} className="border-t border-border align-top" data-testid={`mail-profile-dns-row-${r.kind}`}>
                        <td className="px-2 py-1.5 font-semibold">{r.kind}</td>
                        <td className="px-2 py-1.5 font-mono text-[10px] break-all">{r.host}</td>
                        <td className="px-2 py-1.5 font-mono text-[10px]">{r.type}</td>
                        <td className="px-2 py-1.5 font-mono text-[10px] break-all max-w-[280px]">
                          {r.value}
                          <button onClick={() => copy(r.value)}
                                   className="ml-1 text-primary hover:underline text-[10px]"
                                   data-testid={`mail-profile-dns-copy-${r.kind}`}>Copy</button>
                          {r.note && <div className="text-[10px] text-muted-foreground mt-0.5">{r.note}</div>}
                        </td>
                        <td className="px-2 py-1.5">
                          {!checks ? <span className="text-[10px] text-muted-foreground">Run check</span>
                            : v?.ok ? <span className="text-[10px] text-emerald-700 font-semibold">✓ OK</span>
                            : <span className="text-[10px] text-rose-700 font-semibold">✗ Missing</span>}
                          {v?.found && <div className="text-[9px] font-mono text-muted-foreground truncate max-w-[140px]" title={v.found}>{v.found}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {rec.docs_url && <div className="text-[11px] text-muted-foreground">
              Provider docs: <a href={rec.docs_url} target="_blank" rel="noopener noreferrer" className="text-primary underline">{rec.docs_url}</a>
            </div>}
          </>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, col, children }) {
  return (
    <div className={col || ""}>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
